/**
 * Collection Filters System - Moon Moon Agency v2.1
 * Shopify collection filtering with AJAX updates, custom events, and UI sync
 */

// Initialisation de l'objet queryParams pour stocker les paramètres de requête
Shopify.queryParams = {};

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const COLLECTION_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,
	
	// UI Settings
	mobileBreakpoint: window.TABLET_BREAKPOINT, // 990px — filters switch to mobile layout
	defaultOpenFilters: 3,
	
	// Price Slider Settings
	priceGap: 10,
	
	// Selectors - Using data attributes for better separation of concerns
	selectors: {
		form: '[data-collection-form]',
		grid: '[data-collection-grid]',
		gridCollection: '[data-grid-collection]',
		shopifySection: '.shopify-section', // Keep as class (Shopify generated)
		activeFilters: '[data-active-filters]',
		reloadElements: '[data-collection-reload]',
		sortBy: '[data-sort-by]',
		sortByHidden: '[data-sort-by-hidden]',
		
		// Mobile filters
		mobileFilterWrapper: '[data-mobile-filters]',
		htmlElement: 'html',
		
		// Filter accordions
		filterHeaders: '[data-filter-header]',
		
		// Price sliders
		minRange: '[data-price-min]',
		maxRange: '[data-price-max]',
		sliderTrack: '[data-price-track]',
		minRangeValue: '[data-price-min-value]',
		maxRangeValue: '[data-price-max-value]',
		priceMinValue: '[data-price-min-display]',
		priceMaxValue: '[data-price-max-display]'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Collection Event Emitter
const collectionEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'collection'
	};
	
	return document.dispatchEvent(new CustomEvent(`collection:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const CollectionDOM = {
	get form() { return document.querySelector(COLLECTION_CONFIG.selectors.form); },
	get grid() { return document.querySelector(COLLECTION_CONFIG.selectors.grid); },
	get gridCollection() { return document.querySelector(COLLECTION_CONFIG.selectors.gridCollection); },
	get sortBySelector() { return document.querySelector(COLLECTION_CONFIG.selectors.sortBy); },
	get sortByHidden() { return document.querySelector(COLLECTION_CONFIG.selectors.sortByHidden); },
	get html() { return document.querySelector(COLLECTION_CONFIG.selectors.htmlElement); },
	get mobileFilterWrapper() { return document.querySelector(COLLECTION_CONFIG.selectors.mobileFilterWrapper); },
	
	get activeFilters() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters); },
	get reloadElements() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements); },
	get filterHeaders() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.filterHeaders); },
	get formInputs() { return this.form?.querySelectorAll('input') || []; },
	
	// Price sliders
	get priceSliders() {
		return {
			minRange:    document.querySelector(COLLECTION_CONFIG.selectors.minRange),
			maxRange:    document.querySelector(COLLECTION_CONFIG.selectors.maxRange),
			sliderTrack: document.querySelector(COLLECTION_CONFIG.selectors.sliderTrack),
			minInput:    document.querySelector('[data-price-min-input]'),
			maxInput:    document.querySelector('[data-price-max-input]'),
		};
	},
	
	get shopifySection() {
		const gridElem = this.gridCollection;
		return gridElem?.closest(COLLECTION_CONFIG.selectors.shopifySection);
	}
};

/*********************************************************************************************
 * 🔌 CORE COLLECTION API
 */
const CollectionAPI = {
	async request(url) {
		collectionEmit('api-start', { url });
		
		try {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			
			// Check if response is JSON (Section Rendering API) or HTML (fallback)
			const contentType = response.headers.get('content-type');
			const data = contentType && contentType.includes('application/json') 
				? await response.json() 
				: await response.text();
			
			collectionEmit('api-success', { url, responseType: typeof data });
			return data;
		} catch (error) {
			collectionEmit('api-error', { url, error: error.message });
			throw error;
		}
	},

	getSectionId() {
		const section = CollectionDOM.shopifySection;
		if (!section) {
			throw new Error('Collection grid element or shopify section not found');
		}
		return section.id;
	},

	getSectionIdShort() {
		return this.getSectionId().replace('shopify-section-', '');
	},

	async refreshWithFilters(url_filters) {
		collectionEmit('filter-start', { filters: url_filters });
		
		try {
			const currentUrl = `${window.location.pathname}`;
			const sectionId = this.getSectionIdShort();
			
			// Use Shopify Section Rendering API
			const requestUrl = `${currentUrl}?${url_filters}&sections=${sectionId}`;
			const response = await this.request(requestUrl);
			
			if (!CollectionDOM.grid) {
				throw new Error('Collection grid element not found');
			}

			// Handle different response types
			let newSectionHtml;
			if (typeof response === 'object' && response[sectionId]) {
				// Section Rendering API JSON response
				newSectionHtml = response[sectionId];
			} else if (typeof response === 'string') {
				// Fallback: HTML response
				const newPageEl = document.createElement('DIV');
				newPageEl.innerHTML = response;
				const newSection = newPageEl.querySelector(COLLECTION_CONFIG.selectors.grid);
				if (!newSection) {
					throw new Error('Required collection elements not found in response');
				}
				newSectionHtml = newSection.outerHTML;
			} else {
				throw new Error('Invalid response format');
			}

			this.updateDOM(newSectionHtml, currentUrl, url_filters);
			collectionEmit('filter-success', { filters: url_filters, url: `${currentUrl}?${url_filters}` });
			
		} catch (error) {
			collectionEmit('filter-error', { error: error.message, filters: url_filters });
			
			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-grid-loading');
			}
			
			// Fallback: reload page if AJAX fails
			window.location.search = url_filters;
		}
	},

	updateDOM(newSectionHtml, currentUrl, url_filters) {
		// Create temporary element to parse new HTML
		const tempEl = document.createElement('DIV');
		tempEl.innerHTML = newSectionHtml;

		// Update collection grid content (products only)
		// Bloque la min-height pour éviter le flash "grille vide" pendant le swap
		const newGridCollection = tempEl.querySelector(COLLECTION_CONFIG.selectors.grid);
		if (newGridCollection && CollectionDOM.grid) {
			const oldHeight = CollectionDOM.grid.offsetHeight;
			CollectionDOM.grid.style.minHeight = `${oldHeight}px`;
			CollectionDOM.grid.innerHTML = newGridCollection.innerHTML;
			/* Release min-height après le layout du nouveau contenu (2 RAF = next paint cycle) */
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					CollectionDOM.grid.style.minHeight = '';
				});
			});
		}

		// Capture l'état des accordéons AVANT remplacement du drawer
		// Clé = filter.param_name extrait depuis le 1er input checkbox de la liste,
		// stable même si le label texte ou son count change.
		const accordionStates = new Map();
		const getFilterKey = (header) => {
			const list = header.nextElementSibling;
			if (!list) return '';
			const input = list.querySelector('input[name]');
			return input?.name || '';
		};
		const oldDrawer = document.querySelector(COLLECTION_CONFIG.selectors.mobileFilterWrapper);
		if (oldDrawer) {
			oldDrawer.querySelectorAll(COLLECTION_CONFIG.selectors.filterHeaders).forEach(header => {
				const key = getFilterKey(header);
				if (key) accordionStates.set(key, header.classList.contains('mm-active'));
			});
		}

		// Update drawer filtres (counts par option + options dispo selon sélections aval)
		const newDrawer = tempEl.querySelector(COLLECTION_CONFIG.selectors.mobileFilterWrapper);
		if (newDrawer && oldDrawer) {
			// Capture la position scroll du form (zone scrollable) avant le replace
			// pour la restaurer apres — sinon l'utilisateur est reset en haut a chaque
			// toggle de filtre (mauvaise UX si la liste est longue).
			const oldForm = oldDrawer.querySelector(COLLECTION_CONFIG.selectors.form);
			const savedScrollTop = oldForm?.scrollTop || 0;

			oldDrawer.innerHTML = newDrawer.innerHTML;

			// Restaure le scrollTop sur le nouveau form
			if (savedScrollTop > 0) {
				const newForm = oldDrawer.querySelector(COLLECTION_CONFIG.selectors.form);
				if (newForm) newForm.scrollTop = savedScrollTop;
			}

			// Restaure l'état mm-active des accordéons par matching de param_name
			oldDrawer.querySelectorAll(COLLECTION_CONFIG.selectors.filterHeaders).forEach(header => {
				const key = getFilterKey(header);
				if (accordionStates.get(key)) {
					header.classList.add('mm-active');
				}
			});
		}

		// Update active filters
		const newActiveFilters = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters);
		CollectionDOM.activeFilters.forEach((currentFilter, index) => {
			if (newActiveFilters[index]) {
				currentFilter.innerHTML = newActiveFilters[index].innerHTML;
			}
		});

		// Update other elements with 'data-collection-reload' attribute
		const newReloadElems = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements);
		CollectionDOM.reloadElements.forEach((currentElem, index) => {
			if (newReloadElems[index]) {
				// Check if element has a <span> child (like buttons from mm-btn)
				const currentSpan = currentElem.querySelector('span');
				const newSpan = newReloadElems[index].querySelector('span');

				if (currentSpan && newSpan) {
					// Update only the text inside the span
					currentSpan.textContent = newSpan.textContent;
				} else {
					// Fallback: update entire innerHTML for simple elements
					currentElem.innerHTML = newReloadElems[index].innerHTML;
				}
			}
		});

		// Update URL in history without reloading page
		const newUrl = `${currentUrl}?${url_filters}`;
		window.history.replaceState(null, document.title, newUrl);
		
		if (CollectionDOM.grid) {
			CollectionDOM.grid.classList.remove('mm-grid-loading');
		}
		
		// Re-initialize filters after DOM update (re-bind les inputs + sync checked from URL)
		CollectionFilters.init();

		// Re-initialize price sliders (les nouveaux range inputs n'ont pas leurs listeners)
		PriceSliders.init();

		// Re-initialize add to cart forms after DOM update
		if (typeof window.initAddToCartForms === 'function') {
			window.initAddToCartForms();
		}

		// Re-initialize Splide sliders sur les nouveaux cards produit (skip auto si _splide)
		if (window.SplideManager?.init) {
			window.SplideManager.init();
		}

		/* Re-init btn animate sur les boutons fraichement injectes (drawer replace + cards).
		 * Sans ca, le `text-shadow: 0px 1.3em currentColor` defini pour
		 * `[data-button-animate-chars] span` (mm-btn.css:80) deborde sous le bouton
		 * "Afficher X produits" — le wrapper <div overflow:hidden> que cree btn_animate
		 * sert justement a clipper cette ombre. */
		if (typeof window.initButtonCharacterStagger === 'function') {
			window.initButtonCharacterStagger();
		}
	}
};

/*********************************************************************************************
 * 🔍 COLLECTION FILTERS MANAGEMENT
 */
const CollectionFilters = {
	init() {
		if (!CollectionDOM.form) {
			collectionEmit('form-error', { error: 'Collection facets form not found' });
			return;
		}

		const inputs = CollectionDOM.formInputs;

		// Add event listeners to inputs
		inputs.forEach(input => {
			/* Skip les inputs sans `name` : ils ne contribuent pas au FormData (donc
			 * ne changent pas le set de filtres) mais peuvent declencher un change
			 * parasitaire (ex: <input type="search" data-filter-search> du popup
			 * searchable qui sert juste a filtrer la liste en JS — son blur native
			 * fire un change qui submitait inutilement le form et ecrasait le filtre
			 * vendor fraichement coche). */
			if (!input.name) return;
			// Remove existing listeners to avoid duplicates
			input.removeEventListener('change', this.handleInputChange);
			input.addEventListener('change', this.handleInputChange);
		});

		/* Sync forcée des checkboxes/radios depuis l'URL (workaround robuste
		 * pour les cas où le rendu serveur ou un re-render AJAX n'a pas correctement
		 * propagé l'attribut checked). Source de vérité = URL params. */
		this.syncCheckedFromUrl();
		this.syncCheckboxVisuals();

		collectionEmit('filters-initialized', { inputCount: inputs.length });
	},

	syncCheckedFromUrl() {
		const params = new URLSearchParams(window.location.search);
		if (!CollectionDOM.form) return;
		CollectionDOM.form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(input => {
			if (!input.name) return;
			const values = params.getAll(input.name);
			const shouldBeChecked = values.includes(input.value);
			if (input.checked !== shouldBeChecked) {
				input.checked = shouldBeChecked;
			}
		});
	},

	/* Toggle explicite d'une classe sur CHAQUE label custom checkbox + sur "Tous".
	 * Raison : après innerHTML replace, le selecteur CSS :checked + .label ne se
	 * ré-évalue pas toujours dans certains navigateurs (gotcha connu). Une classe
	 * JS explicite est garantie de matcher l'état réel des inputs. */
	syncCheckboxVisuals() {
		document.querySelectorAll('[data-filter-checkbox-group]').forEach(group => {
			/* 1. Chaque label individuel suit son input frère */
			group.querySelectorAll('.mm-cf-checkbox-input').forEach(input => {
				const label = input.nextElementSibling;
				if (label && label.classList.contains('mm-cf-checkbox')) {
					label.classList.toggle('mm-cf-checkbox--active', input.checked);
				}
			});
			/* 2. "Tous" actif si aucun autre n'est coché */
			const anyChecked = !!group.querySelector('.mm-cf-checkbox-input:checked');
			const allLabel = group.querySelector('[data-filter-checkbox-all]');
			if (allLabel) allLabel.classList.toggle('mm-cf-checkbox-all--active', !anyChecked);
		});
	},

	handleInputChange(event) {
		collectionEmit('input-change', {
			name: event.target.name || event.target.id,
			value: event.target.value,
			checked: event.target.checked
		});

		/* Feedback visuel immédiat sur "Tous" avant l'aller-retour AJAX */
		CollectionFilters.syncCheckboxVisuals();
		CollectionFilters.submitForm();
	},

	async submitForm() {
		if (!CollectionDOM.form || !CollectionDOM.grid) {
			collectionEmit('form-error', { error: 'Required elements not found' });
			return;
		}

		/* Anti-double-submit : si un submit est deja en cours, on queue UN seul
		 * re-submit pour la fin du courant (pour capturer l'etat final apres
		 * d'eventuelles modif de filtres pendant le submit). Sans ce guard, 2
		 * change events rapproches peuvent declencher 2 fetch concurrents dont
		 * le 2e ecrase le 1er (filtre disparait apres ~1s). */
		if (this._isSubmitting) {
			this._pendingResubmit = true;
			return;
		}
		this._isSubmitting = true;

		try {
			collectionEmit('form-submit-start');

			const formData = new FormData(CollectionDOM.form);
			const url_filters = new URLSearchParams(formData).toString();

			CollectionDOM.grid.classList.add('mm-grid-loading');

			await CollectionAPI.refreshWithFilters(url_filters);

			collectionEmit('form-submit-success', { filters: url_filters });

		} catch (error) {
			collectionEmit('form-submit-error', { error: error.message });

			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-grid-loading');
			}
		} finally {
			this._isSubmitting = false;
			/* Si un autre submit a ete queue pendant celui-ci, on le rejoue
			 * (avec l'etat courant du form, post-AJAX et eventuelles re-syncs). */
			if (this._pendingResubmit) {
				this._pendingResubmit = false;
				this.submitForm();
			}
		}
	}
};

/*********************************************************************************************
 * 📱 FILTERS DRAWER (desktop right + mobile fullscreen — logique unifiée)
 */
const MobileFilters = {
	open() {
		const wrapperMobile = CollectionDOM.mobileFilterWrapper;
		const html = CollectionDOM.html;

		if (wrapperMobile && html) {
			wrapperMobile.classList.add('mm-open');
			html.classList.add('stop-scrolling');
			collectionEmit('filters-drawer-opened');
		}
	},

	close() {
		const wrapperMobile = CollectionDOM.mobileFilterWrapper;
		const html = CollectionDOM.html;

		if (wrapperMobile && html) {
			wrapperMobile.classList.remove('mm-open');
			html.classList.remove('stop-scrolling');
			collectionEmit('filters-drawer-closed');
		}
	},

	checkDrawer() {
		const hasOpenFilters = document.location.href.includes('open_filters');
		if (hasOpenFilters) {
			this.open();
		}
	}
};

/*********************************************************************************************
 * 🎛️ FILTER ACCORDIONS
 */
const FilterAccordions = {
	init() {
		/* Activation des 3 premiers UNIQUEMENT au premier load (pas au re-render AJAX
		 * qui doit conserver l'état utilisateur — déjà restauré par CollectionAPI.updateDOM). */
		if (!this._initialized) {
			this._initialized = true;
			CollectionDOM.filterHeaders.forEach((header, index) => {
				if (index < COLLECTION_CONFIG.defaultOpenFilters) {
					header.classList.add('mm-active');
				}
			});

			/* Délégation document → survit aux re-render des accordéons (nouveaux headers
			 * créés par updateDOM bénéficient automatiquement du toggle). */
			document.addEventListener('click', (e) => {
				const header = e.target.closest(COLLECTION_CONFIG.selectors.filterHeaders);
				if (!header) return;
				header.classList.toggle('mm-active');
				collectionEmit('filter-accordion-toggle', { active: header.classList.contains('mm-active') });
			});
		}

		collectionEmit('accordions-initialized', { count: CollectionDOM.filterHeaders.length });
	}
};

/*********************************************************************************************
 * 💰 PRICE SLIDERS
 */
const PriceSliders = {
	get elements() {
		return {
			...CollectionDOM.priceSliders,
			minHidden: document.querySelector('[data-price-min-hidden]'),
			maxHidden: document.querySelector('[data-price-max-hidden]'),
		};
	},

	isReady() {
		const e = this.elements;
		return e.minRange && e.maxRange && e.sliderTrack;
	},

	/* Sync les inputs hidden depuis les ranges. Tout est en EUROS (decimal main currency)
	 * car Shopify URL filter.v.price.gte/lte attend la valeur en unité monétaire principale,
	 * PAS en centimes — sinon ?gte=20000 = "≥ 20000€" et 0 produit. */
	_syncHidden() {
		const { minRange, maxRange, minHidden, maxHidden } = this.elements;
		/* Hidden a la borne de la plage = pas de filtre prix : on le desactive pour qu'il sorte du FormData.
		 * Sinon chaque filtre envoyait ?filter.v.price.gte=0&filter.v.price.lte=<max>, compte comme un
		 * filtre actif dans "Filtrer (n)" au refresh AJAX. */
		if (minHidden) {
			minHidden.value = parseInt(minRange.value) || 0;
			minHidden.disabled = parseInt(minHidden.value) <= (parseInt(minRange.min) || 0);
		}
		if (maxHidden) {
			maxHidden.value = parseInt(maxRange.value) || 0;
			maxHidden.disabled = parseInt(maxHidden.value) >= parseInt(maxRange.max);
		}
	},

	init() {
		if (!this.isReady()) return;

		const { minRange, maxRange, minInput, maxInput } = this.elements;

		/* Range → Input + track (live) */
		minRange.addEventListener('input', () => {
			if (parseInt(minRange.value) > parseInt(maxRange.value) - COLLECTION_CONFIG.priceGap) {
				minRange.value = maxRange.value - COLLECTION_CONFIG.priceGap;
			}
			if (minInput) minInput.value = minRange.value;
			this._syncHidden();
			this.updateTrack();
		});

		maxRange.addEventListener('input', () => {
			if (parseInt(maxRange.value) < parseInt(minRange.value) + COLLECTION_CONFIG.priceGap) {
				maxRange.value = parseInt(minRange.value) + COLLECTION_CONFIG.priceGap;
			}
			if (maxInput) maxInput.value = maxRange.value;
			this._syncHidden();
			this.updateTrack();
		});

		/* Submit AJAX au release (change event sur range) → on appelle submitForm
		 * directement (plutôt que de dispatcher un change sur le hidden qui peut
		 * ne pas être capté correctement par le listener attaché à init). */
		minRange.addEventListener('change', () => {
			this._syncHidden();
			CollectionFilters.submitForm();
		});
		maxRange.addEventListener('change', () => {
			this._syncHidden();
			CollectionFilters.submitForm();
		});

		/* Input → Range + track (debounced submit via blur/Enter) */
		if (minInput) {
			minInput.addEventListener('input', () => {
				const v = Math.max(0, Math.min(parseInt(minInput.value) || 0, parseInt(maxRange.value) - COLLECTION_CONFIG.priceGap));
				minRange.value = v;
				this._syncHidden();
				this.updateTrack();
			});
			minInput.addEventListener('change', () => {
				this._syncHidden();
				CollectionFilters.submitForm();
			});
		}
		if (maxInput) {
			maxInput.addEventListener('input', () => {
				const v = Math.min(parseInt(maxRange.max), Math.max(parseInt(maxInput.value) || 0, parseInt(minRange.value) + COLLECTION_CONFIG.priceGap));
				maxRange.value = v;
				this._syncHidden();
				this.updateTrack();
			});
			maxInput.addEventListener('change', () => {
				this._syncHidden();
				CollectionFilters.submitForm();
			});
		}

		/* Sync initial des hidden (centimes) depuis les visibles (euros) au mount */
		this._syncHidden();
		this.updateTrack();
		collectionEmit('price-sliders-initialized');
	},

	updateTrack() {
		if (!this.isReady()) return;
		const { minRange, maxRange, sliderTrack } = this.elements;
		const minValue = parseInt(minRange.value, 10);
		const maxValue = parseInt(maxRange.value, 10);
		const minPercent = (minValue / minRange.max) * 100;
		const maxPercent = (maxValue / maxRange.max) * 100;
		sliderTrack.style.background = `linear-gradient(to right, #ddd ${minPercent}%, var(--black-900) ${minPercent}%, var(--black-900) ${maxPercent}%, #ddd ${maxPercent}%)`;
		collectionEmit('price-update', { minValue, maxValue, minPercent, maxPercent });
	}
};

/*********************************************************************************************
 * 🔄 SORT BY FUNCTIONALITY
 */
const SortBy = {
	/* Le select page (data-sort-by) est désormais géré par window.mmSortByChanged
	 * appelé via onchange="..." inline. L'ancien listener change qui faisait
	 * location.search = ... provoquait un reload page complet (incompatible
	 * avec le submit AJAX du tunnel filtres). Méthode conservée vide pour
	 * préserver l'API externe (CollectionFilters s'attend à cette init). */
	init() {
		collectionEmit('sort-initialized', {
			currentValue: CollectionDOM.sortBySelector?.value || null,
		});
	}
};

/*********************************************************************************************
 * 🔲 GRID LAYOUT SWITCHER (mobile only) — 1 col ↔ 2 cols
 *    Toggle `.mm-grid-1col` on the grid + persiste le choix en localStorage.
 *    Survit aux re-render AJAX via document delegation.
 */
const GridLayoutSwitcher = {
	STORAGE_KEY: 'mm-grid-cols',

	init() {
		/* Applique le layout sauvegardé (defaut '2') */
		const saved = localStorage.getItem(this.STORAGE_KEY) || '2';
		this._applyLayout(saved);

		/* Délégation document → survit aux re-render des boutons (re-init après AJAX) */
		if (this._delegated) return;
		this._delegated = true;
		document.addEventListener('click', (e) => {
			const btn = e.target.closest('[data-grid-layout]');
			if (!btn) return;
			e.preventDefault();
			const layout = btn.dataset.gridLayout;
			this._applyLayout(layout);
			localStorage.setItem(this.STORAGE_KEY, layout);
		});
	},

	_applyLayout(layout) {
		const grid = document.querySelector('[data-collection-grid]');
		if (!grid) return;
		grid.classList.toggle('mm-grid-1col', layout === '1');
		document.querySelectorAll('[data-grid-layout]').forEach(b => {
			const isActive = b.dataset.gridLayout === layout;
			b.classList.toggle('mm-grid-layout-btn--active', isActive);
			b.setAttribute('aria-pressed', String(isActive));
		});
	},
};

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS (for backward compatibility)
 */
window.mmSortByChanged = (select) => {
	if (!select) {
		collectionEmit('sort-error', { error: 'Sort by select missing' });
		return;
	}
	/* Sync l'input hidden du drawer (source de vérité pour le form submit) */
	const hidden = document.querySelector('[data-sort-hidden]');
	if (hidden) hidden.value = select.value;
	/* Sync visual state des pills shortcut */
	document.querySelectorAll('[data-sort-radio] [data-sort-value]').forEach(b => {
		b.classList.toggle('mm-cf-sort-radio-btn--active', b.dataset.sortValue === select.value);
	});
	/* Legacy : ancien hidden select si encore présent dans le DOM */
	const hiddenSelect = CollectionDOM.sortByHidden;
	if (hiddenSelect) hiddenSelect.value = select.value;

	CollectionFilters.submitForm();
};

// Mobile filter functions
window.mmOpenFiltersMobile = () => MobileFilters.open();
window.mmCloseFiltersMobile = () => MobileFilters.close();
window.mmCheckDrawer = () => MobileFilters.checkDrawer();

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	collectionEmit('init-start');
	
	try {
		CollectionFilters.init();
		FilterAccordions.init();
		PriceSliders.init();
		SortBy.init();
		GridLayoutSwitcher.init();
		MobileFilters.checkDrawer();
		
		collectionEmit('init-success');
	} catch (error) {
		collectionEmit('init-error', { error: error.message });
		window.log('error', 'Collection', 'Collection initialization failed', { error: error.message });
	}
});

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	/* Filter close — delegation document pour binder TOUS les [data-filter-close]
	 * (le bouton X du header ET le bouton 'Voir les X resultats' du footer) et survivre
	 * aux re-render AJAX (le footer button est dans le drawer qui se re-render). */
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-filter-close]')) {
			e.preventDefault();
			if (typeof window.mmCloseFiltersMobile === 'function') {
				window.mmCloseFiltersMobile();
			}
		}
	});

	// Filter submit button
	const filterSubmitBtn = document.querySelector('[data-filter-submit]');
	if (filterSubmitBtn) {
		filterSubmitBtn.addEventListener('click', () => {
			CollectionFilters.submitForm();
		});
	}

	// Open filters mobile buttons
	const openFiltersBtns = document.querySelectorAll('[data-open-filters-mobile]');
	openFiltersBtns.forEach((btn) => {
		btn.addEventListener('click', () => {
			if (typeof window.mmCloseMobileMenu === 'function') {
				window.mmCloseMobileMenu();
			}
			if (typeof window.mmOpenFiltersMobile === 'function') {
				window.mmOpenFiltersMobile();
			}
		});
	});

	// Overlay click → close drawer
	const filterOverlay = document.querySelector('[data-filter-overlay]');
	if (filterOverlay) {
		filterOverlay.addEventListener('click', () => {
			if (typeof window.mmCloseFiltersMobile === 'function') {
				window.mmCloseFiltersMobile();
			}
		});
	}

	/* Bouton "Marques" mobile (bottom bar) → ouvre le popup du 1er filtre searchable
	 * (typiquement le filtre marques). Délégation document pour survivre aux re-render AJAX.
	 * On déclenche le click natif sur [data-searchable-open] qui a déjà tout le JS associé
	 * (focus input, etc.) via _bindSearchablePopups dans mm_filters_custom.js.
	 *
	 * Le drawer doit être ouvert AVANT pour que le popup soit visible (le popup est nesté
	 * dans le drawer). On ouvre donc d'abord le drawer, puis on click le trigger searchable. */
	document.addEventListener('click', (e) => {
		const btn = e.target.closest('[data-open-brands-popup]');
		if (!btn) return;
		e.preventDefault();
		const trigger = document.querySelector('[data-searchable-open]');
		if (!trigger) return;
		if (typeof window.mmOpenFiltersMobile === 'function') {
			window.mmOpenFiltersMobile();
		}
		/* requestAnimationFrame pour laisser le drawer afficher avant l'ouverture popup */
		requestAnimationFrame(() => {
			trigger.click();
			/* Flag pour que le X du popup ferme AUSSI le drawer filtres : l'utilisateur
			 * a cliqué sur "Marques" (bottom bar), pas sur "Filtrer" — quand il ferme
			 * les marques il veut revenir sur la grille, pas rester sur les filtres.
			 * Le handler close (mm_filters_custom.js) lit ce flag puis le delete.
			 * Pose APRES trigger.click() sinon le open handler efface le flag. */
			const popup = trigger.closest('.mm-cf-tag-filter')?.querySelector('[data-searchable-popup]');
			if (popup) popup.dataset.mmClosesDrawer = 'true';
		});
	});

	// Custom checkbox "Tous" → décoche tous les autres + submit
	document.addEventListener('click', (e) => {
		const allLabel = e.target.closest('[data-filter-checkbox-all]');
		if (!allLabel) return;
		e.preventDefault();
		const group = allLabel.closest('[data-filter-checkbox-group]');
		if (!group) return;
		const inputs = group.querySelectorAll('.mm-cf-checkbox-input');
		if (!inputs.length) return;
		const anyChecked = Array.from(inputs).some(i => i.checked);
		if (!anyChecked) return; // already at "Tous" state
		inputs.forEach(i => { i.checked = false; });
		/* Feedback visuel immédiat */
		CollectionFilters.syncCheckboxVisuals();
		inputs[0].dispatchEvent(new Event('change', { bubbles: true }));
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	['init-success', 'filter-success', 'input-change', 'form-submit-start', 'price-update', 'sort-change'].forEach(event => {
		document.addEventListener(`collection:${event}`, e =>
			console.log(`🏷️ [COLLECTION] ${event}:`, e.detail)
		);
	});
}